import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";
import { useMemo } from "react";

import AcquisitionsAPI from "@/api/acquisitions/index.js";
import UserApi from "@/api/users/users";
import { getEvidenceBrands } from "@/api/evidence/index.js";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomField,
  CustomFieldsQuery,
  EvidenceTypesAPI,
  GetEvidenceTypesResponse,
} from "@/Features";
import { BasicUser } from "@/types/Users/Users";

const resolveCustomFieldFilter = (field: CustomField) => {
  const filter: FilterDefinition = {
    label: field.field_name,
    dataField: `custom_field_${field.field_id}`,
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  };

  switch (field.editor_type) {
    case "textBox":
      filter.inputType = "text";
      filter.operators = [
        Operators.Equals,
        Operators.NotEquals,
        Operators.Contains,
        Operators.DoesNotContain,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      break;
    // Known limitation for dropDown/tagBox: the server's custom-field path
    // (queryConditionResolver) matches each value with LIKE '%value%', so
    // In/NIn behave as substring matches — In ["Active"] also matches rows
    // tagged "Inactive". Exact matching needs a resolver change in
    // monolith-api; the conditions format can't express it from here.
    case "dropDown":
      filter.inputType = "multiselect";
      filter.operators = [
        Operators.In,
        Operators.NIn,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      filter.selectOptions =
        field.options?.map((option) => ({
          label: option,
          value: option,
          data: option,
        })) || [];
      break;
    case "dateBox":
      filter.inputType = "datetime";
      filter.resolution = "day";
      // No Equals/NotEquals — see the note on DATE_OPERATORS below.
      filter.operators = [
        Operators.GreaterThan,
        Operators.LessThan,
        Operators.Between,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      break;
    case "tagBox":
      filter.inputType = "multiselect";
      filter.operators = [
        Operators.In,
        Operators.NIn,
        Operators.IsEmpty,
        Operators.IsNotEmpty,
      ];
      filter.selectOptions =
        field.options?.map((option) => ({
          label: option,
          value: option,
          data: option,
        })) || [];
      break;
    default:
      break;
  }

  return filter;
};

// Operator sets are limited to what the acquisitions query pipeline
// (queryConditionResolver) can express: no >=, <=, notBetween, and no
// "is not empty" on standard (non-custom) fields.
const TEXT_OPERATORS = [
  Operators.Equals,
  Operators.NotEquals,
  Operators.Contains,
  Operators.DoesNotContain,
  Operators.IsEmpty,
];

const MULTISELECT_OPERATORS = [Operators.In, Operators.NIn, Operators.IsEmpty];

// Date filters expose only After/Before/Between (+ is-empty): the resolver
// runs raw `= ?` against full ISO timestamps, so day-resolution Equals/
// NotEquals would silently match nothing/everything.
const DATE_OPERATORS = [
  Operators.GreaterThan,
  Operators.LessThan,
  Operators.Between,
  Operators.IsEmpty,
];

export const useAcquisitionFilterDefinitions = () => {
  const { data: customFields } = CustomFieldsQuery.useGetCustomFields({
    category: CUSTOM_FIELD_CATEGORIES.ACQUISITION,
  });

  const baseFilters: FilterDefinition[] = useMemo(() => {
    return [
      {
        label: "Acquisition Name",
        pluralLabel: "Acquisition Names",
        dataField: "acq_name",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Format",
        pluralLabel: "Formats",
        dataField: "format",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["acquisition:formats:list"]),
          loadPage: async () => {
            const page = (await AcquisitionsAPI.getFormats()) as {
              format: string;
              format_id: number;
            }[];

            return {
              items: page.map((item) => ({
                label: item.format,
                value: item.format,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Type",
        pluralLabel: "Types",
        dataField: "acq_type",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["acquisition:types:list"]),
          loadPage: async () => {
            const page = (await AcquisitionsAPI.getTypes()) as {
              type: string;
              type_id: number;
            }[];

            return {
              items: page.map((item) => ({
                label: item.type,
                value: item.type,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Status",
        pluralLabel: "Statuses",
        dataField: "status",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["acquisitions:statuses"]),
          loadPage: async () => {
            const page = (await AcquisitionsAPI.getStatuses()) as {
              status: string;
              status_id: number;
            }[];

            return {
              items: page.map((item) => ({
                label: item.status,
                value: item.status,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Acquired By",
        pluralLabel: "Acquired By",
        dataField: "acquired_by_id",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () =>
            JSON.stringify([
              "users",
              "list",
              { includeObservers: false, includeInactive: false },
            ]),
          loadPage: async () => {
            const page = (await UserApi.getUsers({
              includeObservers: false,
              includeInactive: false,
            })) as BasicUser[];

            return {
              items: page.map((user) => ({
                label: user.full_name,
                value: user.user_id.toString(),
                data: user,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Forensic Software",
        pluralLabel: "Forensic Software",
        dataField: "acq_tool",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["acquisition:software:list"]),
          loadPage: async () => {
            const page = (await AcquisitionsAPI.getSoftwareNames()) as {
              software_name: string;
            }[];

            return {
              items: page.map((item) => ({
                label: item.software_name,
                value: item.software_name,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Software Version",
        pluralLabel: "Software Versions",
        dataField: "tool_version",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Created On",
        dataField: "created_on",
        inputType: "datetime",
        resolution: "day",
        operators: DATE_OPERATORS,
      },
      {
        label: "Acquire Date",
        dataField: "acquire_date",
        inputType: "datetime",
        resolution: "day",
        operators: DATE_OPERATORS,
      },
      {
        label: "Hash 1",
        pluralLabel: "Hash 1",
        dataField: "hash_1",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Hash 1 Algorithm",
        pluralLabel: "Hash 1 Algorithms",
        dataField: "hash_1_type",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["acquisition:hash_types:list"]),
          loadPage: async () => {
            const page = (await AcquisitionsAPI.getHashTypes()) as {
              hash_type: string;
            }[];

            return {
              items: page.map((item) => ({
                label: item.hash_type,
                value: item.hash_type,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Hash 2",
        pluralLabel: "Hash 2",
        dataField: "hash_2",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Hash 2 Algorithm",
        pluralLabel: "Hash 2 Algorithms",
        dataField: "hash_2_type",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["acquisition:hash_types:list"]),
          loadPage: async () => {
            const page = (await AcquisitionsAPI.getHashTypes()) as {
              hash_type: string;
            }[];

            return {
              items: page.map((item) => ({
                label: item.hash_type,
                value: item.hash_type,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Evidence Number",
        pluralLabel: "Evidence Numbers",
        dataField: "evidence_number",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Evidence UUID",
        pluralLabel: "Evidence UUIDs",
        dataField: "evidence_uuid",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Evidence Name",
        pluralLabel: "Evidence Names",
        dataField: "evidence_item_name",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Evidence Model Number",
        pluralLabel: "Evidence Model Numbers",
        dataField: "evidence_model_number",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Evidence Unique ID",
        pluralLabel: "Evidence Unique IDs",
        dataField: "evidence_unique_id",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Evidence Type",
        pluralLabel: "Evidence Types",
        dataField: "evidence_type",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["evidence:types"]),
          loadPage: async () => {
            const page =
              (await EvidenceTypesAPI.get()) as GetEvidenceTypesResponse;

            return {
              items: page.map((evidenceType) => ({
                label: evidenceType.evi_type,
                value: evidenceType.evi_type,
                data: evidenceType,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Evidence Provider",
        pluralLabel: "Evidence Providers",
        dataField: "evidence_provider",
        inputType: "multiselect",
        operators: MULTISELECT_OPERATORS,
        query: {
          initialPageParam: 1,
          cacheKey: () => JSON.stringify(["evidence:providers"]),
          loadPage: async () => {
            const page = (await getEvidenceBrands()) as {
              manufacturer: string;
            }[];

            return {
              items: page.map((item) => ({
                label: item.manufacturer,
                value: item.manufacturer,
                data: item,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Linked Contact",
        pluralLabel: "Linked Contacts",
        dataField: "linked_contact",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Storage",
        pluralLabel: "Storage",
        dataField: "storage_number",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Description",
        pluralLabel: "Descriptions",
        dataField: "description",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
      {
        label: "Location Path",
        pluralLabel: "Location Paths",
        dataField: "location_path",
        inputType: "text",
        operators: TEXT_OPERATORS,
      },
    ];
  }, []);

  const customFieldFilters: FilterDefinition[] = useMemo(() => {
    return customFields?.data?.map(resolveCustomFieldFilter) || [];
  }, [customFields]);

  // Memoized so the returned array keeps a stable identity across renders —
  // otherwise every render feeds a fresh `filters` reference into the Table and
  // the export/query callbacks that depend on it.
  return useMemo(
    () => [...baseFilters, ...customFieldFilters],
    [baseFilters, customFieldFilters]
  );
};
